import { useState, useEffect, useRef } from 'react';
import { Upload, FileText, AlertTriangle, CheckCircle, Clock, TrendingUp,
         ChevronDown, ChevronUp, Trash2, RefreshCw, Shield, DollarSign,
         Wrench, Users, AlertCircle, Info, Zap, Plus, Mail, X, Save } from 'lucide-react';

const STORE_URL = '/.netlify/functions/ap-intelligence-store';

// ── Helpers ───────────────────────────────────────────────────────────────────

async function fetchDocs() {
  try {
    const r = await fetch(STORE_URL);
    const d = await r.json();
    return d.docs || [];
  } catch { return []; }
}

async function serverSave(doc) {
  await fetch(STORE_URL, { method:'POST', headers:{'Content-Type':'application/json'},
    body: JSON.stringify({ action:'save', doc }) });
}

async function serverDelete(id) {
  await fetch(STORE_URL, { method:'POST', headers:{'Content-Type':'application/json'},
    body: JSON.stringify({ action:'delete', id }) });
}

const SYSTEM = `You are the intelligence analyst for the Australia Post delivery-partner business run by PH Bouveng Pty Ltd, trading as Hollypaul / HPR (the related recruitment entity is Hollypaul Recruitment Pty Ltd, ABN 60 651 664 885).
BUSINESS: ~41 subcontractor delivery drivers across inner Sydney (City, Darlinghurst, Broadway) delivering for Australia Post, plus a Night Sort operation at the Sydney CBD chutes (005/006/015).
Driver pay is set per parcel and measured against the Courier & Taxi Truck Contract Determination (CTTCD) minimum hourly safety net; night sort labour references the Road Transport Distribution Award 2020 [MA000038] / RTCCO.
Live issues: CTTCD/MSO minimum rates, the TWU Minimum Standards Order applications before the Fair Work Commission (FWC), proposed per-kilometre rates, superannuation, record-keeping, fuel cost recovery (RTCCO), payroll tax.
The principal reader is Paul Bouveng (owner). "We"/"us" = this business.

YOUR JOB: read the document, work out what — if anything — it actually requires of THIS business, and produce a precise brief. Accuracy and restraint matter far more than volume. A wrong or invented task is worse than none.

STEP 1 — classify the document's intent (docIntent):
  "asks_us_to_respond"      it asks us to answer, provide, complete, attend, sign, or reply to something
  "for_awareness"           informational (newsletter, policy, update) that may affect us but asks nothing directly
  "our_response_or_record"  a document WE wrote or sent, or an internal record — not a new task from outside
  "not_relevant"            nothing in it bears on this business

STEP 2 — if docIntent is "asks_us_to_respond", list each concrete thing being asked of us in whatTheyWant (one plain-text item per real request, in the document's own terms). Otherwise whatTheyWant is [].

STEP 3 — findings. Create a finding ONLY where the document genuinely requires us to act, or materially affects us.
  - Do NOT invent generic compliance, finance or admin tasks that the document does not itself raise.
  - If the document's purpose is to ask us questions or request a reply, the findings ARE those requests — one finding per real ask — not tangential ideas of your own.
  - For an "our_response_or_record" document, return few or no findings; it is a record, not a new task.
  - Every finding must trace to specific text in the document (sourceText).
  - For each finding, suggestedResponse is a concise, practical draft of how we should reply or act on that item — in our voice, ready for Paul to use or adapt.
Anything you considered but judged not to require action goes in notRelevant with a short reason.

CRITICAL JSON RULES — follow exactly:
1. Return ONLY the JSON object — no text before or after
2. All string values on a single line — NO literal newlines inside strings
3. Paraphrase instead of using double quotes inside string values
4. Keep sourceText under 100 characters, no apostrophes or quotes
5. workMethod steps separated by " | " not newlines
6. whatTheyWant items and suggestedResponse are plain single-line strings
7. The entire response must be valid JSON parseable by JSON.parse()

Return this structure:
{"docTitle":"string","docDate":"string","docType":"newsletter|briefing|forum|email|policy|questionnaire|response","docIntent":"asks_us_to_respond|for_awareness|our_response_or_record|not_relevant","executiveSummary":"2-3 sentences no quotes","whatTheyWant":["plain text ask"],"findings":[{"id":"F1","title":"short title","priority":"critical|high|medium|low","category":"compliance|financial|operations|contract|safety|people|opportunity","relevance":"plain text no special chars","deadline":"date or null","action":"plain text action","suggestedResponse":"plain text recommended reply or action","workMethod":"step 1 | step 2 | step 3","owner":"Paul Bouveng or Finance or Operations","sourceText":"short paraphrase under 100 chars"}],"notRelevant":["item - reason"]}`;

// Shared Claude call — used by both PDF upload and paste-in. Returns parsed analysis + token usage.
async function analyseText(text, filename) {
  const keyResp = await fetch('/.netlify/functions/get-claude-key');
  const { key } = await keyResp.json();
  if (!key) throw new Error('Could not get API key');

  const today = new Date().toISOString().slice(0,10);
  const dateGuide = `Today's date is ${today}. When a deadline or date appears without a year, assume the current year (${today.slice(0,4)}) or the next future occurrence — never a past year unless the document explicitly states one. Return deadlines in YYYY-MM-DD format.`;

  const userText = (text && text.trim().length > 0)
    ? `${dateGuide}\n\nFile: ${filename}\n\n${text}\n\nAnalyse and return the JSON intelligence brief.`
    : `${dateGuide}\n\nFile: ${filename}. Analyse and return JSON intelligence brief.`;

  const claudeResp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type':'application/json', 'x-api-key': key, 'anthropic-version':'2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-5',
      max_tokens: 3000,
      system: SYSTEM,
      messages: [{ role:'user', content:[{ type:'text', text: userText }] }],
    }),
  });

  if (!claudeResp.ok) {
    const errText = await claudeResp.text();
    throw new Error(`Claude error ${claudeResp.status}: ${errText.substring(0,200)}`);
  }
  const claudeData = await claudeResp.json();
  let raw = (claudeData.content?.filter(b => b.type==='text').map(b => b.text).join('') || '')
    .replace(/^```json\s*/i,'').replace(/^```\s*/i,'').replace(/\s*```$/i,'').trim();

  const jsonStart = raw.indexOf('{');
  const jsonEnd   = raw.lastIndexOf('}');
  if (jsonStart > 0 || jsonEnd < raw.length - 1) raw = raw.slice(jsonStart, jsonEnd + 1);

  // Strip ALL control chars including newlines from Claude output
  // eslint-disable-next-line no-control-regex
  raw = raw.replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, ' ').replace(/\n/g, ' ').replace(/\r/g, '').replace(/\t/g, ' ');

  let analysisResult;
  try { analysisResult = JSON.parse(raw); }
  catch { throw new Error('Claude returned malformed JSON — please try again'); }

  return { analysisResult, usage: claudeData.usage || {} };
}

// claude-sonnet-4-5 pricing → AUD per-doc cost record
function usageToCost(usage) {
  const inputTokens  = usage.input_tokens  || 0;
  const outputTokens = usage.output_tokens || 0;
  const costUSD = (inputTokens * 3 + outputTokens * 15) / 1_000_000;
  return { inputTokens, outputTokens, costUSD: +costUSD.toFixed(4), costAUD: +(costUSD * 1.55).toFixed(4) };
}

const PRIORITY_CONFIG = {
  critical: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.3)',  label: '🔴 Critical', order: 0 },
  high:     { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', label: '🟠 High',     order: 1 },
  medium:   { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.3)', label: '🔵 Medium',   order: 2 },
  low:      { color: '#6b7280', bg: 'rgba(107,114,128,0.1)',border: 'rgba(107,114,128,0.3)','label': '⚪ Low',    order: 3 },
};

const CATEGORY_ICONS = {
  compliance:  <Shield size={13} />,
  financial:   <DollarSign size={13} />,
  operations:  <Wrench size={13} />,
  contract:    <FileText size={13} />,
  safety:      <AlertTriangle size={13} />,
  people:      <Users size={13} />,
  opportunity: <TrendingUp size={13} />,
};

// How the document relates to us — drives the badge + "what they want" section.
const DOC_INTENT = {
  asks_us_to_respond:     { label: '📨 Reply needed', color: '#f59e0b' },
  for_awareness:          { label: '👁 For awareness', color: '#3b82f6' },
  our_response_or_record: { label: '📤 Our response / record', color: '#22c55e' },
  not_relevant:           { label: '— Not relevant', color: '#6b7280' },
};

function fmt(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' }); }
  catch { return d; }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  const c = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.low;
  return <span style={{ padding:'2px 10px', borderRadius:99, background:c.bg, color:c.color, border:`1px solid ${c.border}`, fontSize:11, fontWeight:700 }}>{c.label}</span>;
}

function CategoryBadge({ category }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:99, background:'var(--hp-bg)', border:'1px solid var(--hp-border)', fontSize:11, color:'var(--hp-dim)', fontWeight:600 }}>
      {CATEGORY_ICONS[category] || <Info size={13}/>} {category}
    </span>
  );
}

const CASE_STATUSES = [
  { id:'open',              label:'Open',               color:'#ef4444' },
  { id:'in_progress',       label:'In Progress',        color:'#f59e0b' },
  { id:'awaiting_response', label:'Awaiting Response',  color:'#3b82f6' },
  { id:'resolved',          label:'Resolved',           color:'#22c55e' },
];

function LetterModal({ finding, docTitle, position, onClose }) {
  const [recipient, setRecipient] = useState('accountant');
  const [copied, setCopied] = useState(false);

  const accountantLetter = `Dear [Accountant Name],

Re: Professional Advice — ${finding.title}

I am writing to seek your professional advice regarding the following matter that has arisen from our contract with Australia Post.

BACKGROUND
Australia Post has raised the following issue via their document "${docTitle}":
"${finding.sourceText || finding.relevance}"

OUR CURRENT UNDERSTANDING
${position || '[Please add your position notes to this finding]'}

QUESTIONS FOR YOUR ADVICE
1. How does this obligation apply to Hollypaul Recruitment Pty Ltd specifically?
2. What steps should we take to ensure compliance?
3. Are there any financial or tax implications we should be aware of?
4. What documentation should we maintain?

We would appreciate your written advice at your earliest convenience.

Yours sincerely,

Paul Bouveng
Director — Hollypaul Recruitment Pty Ltd
ABN: 60 651 664 885
paul@hollypaul.com`;

  const apLetter = `Dear [AP Contact Name],

Re: ${finding.title} — Response to ${docTitle}

Thank you for bringing the above matter to our attention.

We have reviewed your communication and note the following:
"${finding.sourceText || finding.relevance}"

OUR POSITION
${position || '[Please add your position notes to this finding before sending]'}

We have sought independent professional advice on this matter and will respond further once that advice has been received.

In the meantime, we are taking the following steps:
${finding.action}

We look forward to working through this matter with you and welcome any further clarification you are able to provide.

Yours sincerely,

Paul Bouveng
Director — Hollypaul Recruitment Pty Ltd
ABN: 60 651 664 885
paul@hollypaul.com
T: [Your phone]`;

  const letter = recipient === 'accountant' ? accountantLetter : apLetter;

  function copy() {
    navigator.clipboard?.writeText(letter).catch(()=>{});
    setCopied(true); setTimeout(()=>setCopied(false), 2000);
  }

  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:'var(--hp-surface)', borderRadius:16, width:'100%', maxWidth:620, maxHeight:'85vh', display:'flex', flexDirection:'column', border:'1px solid var(--hp-border)' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--hp-border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:800, fontSize:16 }}>Compose Letter</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--hp-dim)' }}><X size={20}/></button>
        </div>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--hp-border)', display:'flex', gap:8 }}>
          {[{id:'accountant',label:'To Accountant'},{id:'ap',label:'To Australia Post'}].map(r=>(
            <button key={r.id} onClick={()=>setRecipient(r.id)} style={{ padding:'6px 16px', borderRadius:8, border:`2px solid ${recipient===r.id?'var(--hp-gold)':'var(--hp-border)'}`, background:recipient===r.id?'rgba(201,168,76,0.1)':'var(--hp-bg)', color:recipient===r.id?'var(--hp-gold)':'var(--hp-dim)', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font-body)' }}>{r.label}</button>
          ))}
        </div>
        <div style={{ flex:1, overflow:'auto', padding:'16px 20px' }}>
          <textarea value={letter} readOnly style={{ width:'100%', minHeight:340, padding:14, borderRadius:8, border:'1px solid var(--hp-border)', background:'var(--hp-bg)', color:'var(--hp-text)', fontSize:13, lineHeight:1.7, fontFamily:'var(--font-body)', resize:'vertical', boxSizing:'border-box' }}/>
        </div>
        <div style={{ padding:'12px 20px', borderTop:'1px solid var(--hp-border)', display:'flex', gap:8 }}>
          <button onClick={copy} style={{ flex:1, padding:'9px', borderRadius:8, background:copied?'#22c55e':'var(--hp-gold)', color:'#000', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font-body)', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            <Mail size={14}/>{copied?'Copied!':'Copy Letter'}
          </button>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:8, background:'none', border:'1px solid var(--hp-border)', color:'var(--hp-dim)', fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'var(--font-body)' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function FindingCard({ finding, docTitle, onUpdateFinding }) {
  const [open, setOpen]               = useState(false);
  const [position, setPosition]       = useState(finding._position || '');
  const [caseStatus, setCaseStatus]   = useState(finding._caseStatus || 'open');
  const [tasks, setTasks]             = useState(finding._tasks || []);
  const [newTask, setNewTask]         = useState('');
  const [showLetter, setShowLetter]   = useState(false);
  const [dirty, setDirty]             = useState(false);
  const pc = PRIORITY_CONFIG[finding.priority] || PRIORITY_CONFIG.low;
  const resolved = caseStatus === 'resolved';

  function save() {
    onUpdateFinding({ ...finding, _position:position, _caseStatus:caseStatus, _tasks:tasks, _actioned:resolved });
    setDirty(false);
  }

  function addTask() {
    if (!newTask.trim()) return;
    const t = { id:Date.now().toString(), text:newTask.trim(), status:'open', owner:'Paul Bouveng', due:'' };
    setTasks(prev=>[...prev,t]); setNewTask(''); setDirty(true);
  }

  function updateTask(id, patch) { setTasks(prev=>prev.map(t=>t.id===id?{...t,...patch}:t)); setDirty(true); }
  function removeTask(id) { setTasks(prev=>prev.filter(t=>t.id!==id)); setDirty(true); }

  const statusCfg = CASE_STATUSES.find(s=>s.id===caseStatus) || CASE_STATUSES[0];

  return (
    <>
    {showLetter && <LetterModal finding={finding} docTitle={docTitle} position={position} onClose={()=>setShowLetter(false)}/>}
    <div style={{ border:`1px solid ${resolved?'var(--hp-border)':pc.border}`, borderLeft:`4px solid ${resolved?'#22c55e':pc.color}`, borderRadius:10, background:resolved?'var(--hp-bg)':pc.bg, marginBottom:10, opacity:resolved?0.75:1 }}>
      {/* Header */}
      <div style={{ padding:'12px 16px', cursor:'pointer', display:'flex', gap:12, alignItems:'flex-start' }} onClick={()=>setOpen(o=>!o)}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center', marginBottom:6 }}>
            <PriorityBadge priority={finding.priority}/>
            <CategoryBadge category={finding.category}/>
            {finding.deadline && <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#ef4444', fontWeight:600 }}><Clock size={11}/> Due: {finding.deadline}</span>}
            <span style={{ fontSize:11, fontWeight:700, color:statusCfg.color, background:statusCfg.color+'18', padding:'2px 8px', borderRadius:99, border:`1px solid ${statusCfg.color}40` }}>{statusCfg.label}</span>
            {tasks.length>0 && <span style={{ fontSize:11, color:'var(--hp-dim)' }}>{tasks.filter(t=>t.status==='done').length}/{tasks.length} tasks</span>}
          </div>
          <div style={{ fontWeight:700, fontSize:14, color:resolved?'var(--hp-dim)':'var(--hp-text)' }}>{finding.title}</div>
          <div style={{ fontSize:12, color:'var(--hp-dim)', marginTop:3 }}>{docTitle}</div>
        </div>
        {open?<ChevronUp size={16} color="var(--hp-dim)"/>:<ChevronDown size={16} color="var(--hp-dim)"/>}
      </div>

      {open && (
        <div style={{ padding:'0 16px 16px', borderTop:'1px solid var(--hp-border)', paddingTop:14, display:'grid', gap:12 }}>

          {/* AP's position */}
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>AP's Position / Why This Matters</div>
            <div style={{ fontSize:13, lineHeight:1.6, color:'var(--hp-text)' }}>{finding.relevance}</div>
          </div>

          {/* Action + Work Method */}
          <div style={{ background:'var(--hp-surface)', borderRadius:8, padding:'12px 14px', border:'1px solid var(--hp-border)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Suggested Action</div>
            <div style={{ fontSize:13, lineHeight:1.6, color:'var(--hp-text)', fontWeight:500, marginBottom:10 }}>{finding.action}</div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--hp-gold)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Work Method</div>
            <div style={{ fontSize:13, lineHeight:1.8, color:'var(--hp-text)', whiteSpace:'pre-line' }}>{finding.workMethod}</div>
          </div>

          {/* Suggested response — AI-drafted reply/action for this specific item */}
          {finding.suggestedResponse && (
            <div style={{ background:'rgba(34,197,94,0.06)', borderRadius:8, padding:'12px 14px', border:'1px solid rgba(34,197,94,0.25)' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#22c55e', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Suggested Response</div>
              <div style={{ fontSize:13, lineHeight:1.6, color:'var(--hp-text)' }}>{finding.suggestedResponse}</div>
            </div>
          )}

          {/* Source */}
          {finding.sourceText && (
            <div style={{ fontSize:11, color:'var(--hp-dim)', fontStyle:'italic', lineHeight:1.5, borderLeft:'2px solid var(--hp-border)', paddingLeft:10 }}>
              "{finding.sourceText}"
            </div>
          )}

          {/* ── OUR POSITION ── */}
          <div style={{ background:'rgba(59,130,246,0.06)', borderRadius:8, padding:'12px 14px', border:'1px solid rgba(59,130,246,0.25)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#3b82f6', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Our Position & Objective View</div>
            <textarea
              value={position}
              onChange={e=>{setPosition(e.target.value);setDirty(true);}}
              placeholder="Add your notes, objective view, and context here. e.g. Our tender price never included payroll tax. AP's position is that it should not be included. We need professional advice from our accountant before responding."
              style={{ width:'100%', minHeight:90, padding:'10px 12px', borderRadius:8, border:'1px solid rgba(59,130,246,0.3)', background:'var(--hp-bg)', color:'var(--hp-text)', fontSize:13, lineHeight:1.6, fontFamily:'var(--font-body)', resize:'vertical', boxSizing:'border-box' }}
            />
          </div>

          {/* ── CASE STATUS ── */}
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Case Status</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {CASE_STATUSES.map(s=>(
                <button key={s.id} onClick={()=>{setCaseStatus(s.id);setDirty(true);}}
                  style={{ padding:'5px 14px', borderRadius:8, border:`2px solid ${caseStatus===s.id?s.color:'var(--hp-border)'}`, background:caseStatus===s.id?s.color+'18':'var(--hp-bg)', color:caseStatus===s.id?s.color:'var(--hp-dim)', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'var(--font-body)' }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── ACTION PLAN ── */}
          <div style={{ background:'var(--hp-bg)', borderRadius:8, padding:'12px 14px', border:'1px solid var(--hp-border)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Action Plan</div>

            {tasks.map(t=>(
              <div key={t.id} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:8, padding:'8px 10px', background:'var(--hp-surface)', borderRadius:8, border:'1px solid var(--hp-border)' }}>
                <input type="checkbox" checked={t.status==='done'} onChange={e=>updateTask(t.id,{status:e.target.checked?'done':'open'})} style={{ marginTop:3, flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:t.status==='done'?'var(--hp-dim)':'var(--hp-text)', textDecoration:t.status==='done'?'line-through':'none' }}>{t.text}</div>
                  <div style={{ display:'flex', gap:8, marginTop:4, flexWrap:'wrap' }}>
                    <input value={t.owner} onChange={e=>updateTask(t.id,{owner:e.target.value})} placeholder="Owner" style={{ fontSize:11, padding:'2px 6px', borderRadius:4, border:'1px solid var(--hp-border)', background:'var(--hp-bg)', color:'var(--hp-dim)', fontFamily:'var(--font-body)', width:120 }}/>
                    <input type="date" value={t.due} onChange={e=>updateTask(t.id,{due:e.target.value})} style={{ fontSize:11, padding:'2px 6px', borderRadius:4, border:'1px solid var(--hp-border)', background:'var(--hp-bg)', color:'var(--hp-dim)', fontFamily:'var(--font-body)' }}/>
                  </div>
                </div>
                <button onClick={()=>removeTask(t.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--hp-dim)', padding:2, flexShrink:0 }}><X size={13}/></button>
              </div>
            ))}

            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <input value={newTask} onChange={e=>setNewTask(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addTask()} placeholder="Add task (e.g. Seek accountant advice on payroll tax)" style={{ flex:1, padding:'7px 10px', borderRadius:8, border:'1px solid var(--hp-border)', background:'var(--hp-surface)', color:'var(--hp-text)', fontSize:13, fontFamily:'var(--font-body)' }}/>
              <button onClick={addTask} style={{ padding:'7px 12px', borderRadius:8, background:'var(--hp-gold)', border:'none', color:'#000', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'var(--font-body)', display:'flex', alignItems:'center', gap:4 }}><Plus size={13}/> Add</button>
            </div>
          </div>

          {/* ── BUTTONS ── */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {dirty && (
              <button onClick={save} style={{ padding:'8px 18px', borderRadius:8, background:'#22c55e', border:'none', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'var(--font-body)', display:'flex', alignItems:'center', gap:6 }}>
                <Save size={13}/> Save Changes
              </button>
            )}
            <button onClick={()=>setShowLetter(true)} style={{ padding:'8px 16px', borderRadius:8, background:'var(--hp-surface)', border:'1px solid var(--hp-border)', color:'var(--hp-text)', fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'var(--font-body)', display:'flex', alignItems:'center', gap:6 }}>
              <Mail size={13}/> Compose Letter
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// Lets older docs (uploaded before Drive storage existed) get their original
// PDF attached without re-analysing — preserves all findings and notes.
function AttachPdfButton({ onAttach }) {
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { alert('PDF files only'); return; }
    if (file.size > 4.5 * 1024 * 1024) { alert('PDF too large (max 4.5MB)'); return; }
    setBusy(true);
    try { await onAttach(file); } catch (err) { alert('Attach failed: ' + err.message); } finally { setBusy(false); }
  }
  return (
    <label onClick={e=>e.stopPropagation()} style={{ marginLeft:8, color:'var(--hp-gold)', fontWeight:600, cursor:'pointer' }}>
      {busy ? '· Attaching…' : '· Attach PDF'}
      <input type="file" accept=".pdf" style={{ display:'none' }} onChange={pick} disabled={busy}/>
    </label>
  );
}

function DocCard({ doc, onDelete, onUpdateFinding, onAttachPdf, globalFilter }) {
  const [open, setOpen] = useState(false);

  const findings = (doc.data?.findings || [])
    .slice()
    .sort((a,b) => (PRIORITY_CONFIG[a.priority]?.order ?? 9) - (PRIORITY_CONFIG[b.priority]?.order ?? 9));

  // Respect global filter from dashboard tiles
  const visible = !globalFilter || globalFilter === 'all'
    ? findings
    : findings.filter(f => f.priority === globalFilter);

  const counts = { critical:0, high:0, medium:0, low:0 };
  findings.forEach(f => { if (counts[f.priority] !== undefined) counts[f.priority]++; });

  if (globalFilter && globalFilter !== 'all' && visible.length === 0) return null;

  return (
    <div style={{ background:'var(--hp-surface)', border:'1px solid var(--hp-border)', borderRadius:12, marginBottom:20 }}>
      <div style={{ padding:'16px 20px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <FileText size={16} color="var(--hp-gold)"/>
            <span style={{ fontWeight:800, fontSize:15, color:'var(--hp-text)' }}>{doc.data?.docTitle || doc.filename}</span>
            <span style={{ fontSize:11, color:'var(--hp-dim)', background:'var(--hp-bg)', padding:'2px 8px', borderRadius:99, border:'1px solid var(--hp-border)' }}>{doc.data?.docType}</span>
            {doc.data?.docIntent && DOC_INTENT[doc.data.docIntent] && (
              <span style={{ fontSize:11, fontWeight:700, color:DOC_INTENT[doc.data.docIntent].color, background:DOC_INTENT[doc.data.docIntent].color+'18', padding:'2px 8px', borderRadius:99, border:`1px solid ${DOC_INTENT[doc.data.docIntent].color}40` }}>{DOC_INTENT[doc.data.docIntent].label}</span>
            )}
          </div>
          <div style={{ fontSize:12, color:'var(--hp-dim)', marginBottom:8 }}>
            {doc.data?.docDate} · Uploaded {fmt(doc.uploadedAt)}
            {doc.usage?.costAUD > 0 && <span style={{ marginLeft:8, color:'#22c55e', fontWeight:600 }}>· A${doc.usage.costAUD.toFixed(4)}</span>}
            {doc.pdfUrl
              ? <a href={doc.pdfUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{ marginLeft:8, color:'var(--hp-gold)', fontWeight:600, textDecoration:'none' }}>· View PDF ↗</a>
              : <AttachPdfButton onAttach={file => onAttachPdf(doc.id, file)}/>}
            {(doc.attachments || []).map((a,i) => (
              <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{ marginLeft:8, color:'var(--hp-gold)', fontWeight:600, textDecoration:'none' }}>· {a.label || a.filename} ↗</a>
            ))}
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {Object.entries(counts).filter(([,v])=>v>0).map(([p,v])=>{
              const c = PRIORITY_CONFIG[p];
              return <span key={p} style={{ fontSize:11, fontWeight:700, color:c.color, background:c.bg, border:`1px solid ${c.border}`, padding:'2px 8px', borderRadius:99 }}>{v} {p}</span>;
            })}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={e=>{ e.stopPropagation(); onDelete(doc.id); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--hp-dim)', padding:4 }}>
            <Trash2 size={15}/>
          </button>
          {open ? <ChevronUp size={16} color="var(--hp-dim)"/> : <ChevronDown size={16} color="var(--hp-dim)"/>}
        </div>
      </div>

      {open && (
        <div style={{ padding:'0 20px 20px', borderTop:'1px solid var(--hp-border)', paddingTop:16 }}>
          {doc.data?.executiveSummary && (
            <div style={{ background:'var(--hp-bg)', borderRadius:8, padding:'12px 14px', marginBottom:16, fontSize:13, lineHeight:1.6, color:'var(--hp-text)', border:'1px solid var(--hp-border)' }}>
              <strong>Summary: </strong>{doc.data.executiveSummary}
            </div>
          )}
          {doc.data?.whatTheyWant?.length > 0 && (
            <div style={{ background:'rgba(245,158,11,0.06)', borderRadius:8, padding:'12px 14px', marginBottom:16, border:'1px solid rgba(245,158,11,0.25)' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#f59e0b', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>What they’re asking us for</div>
              <ul style={{ margin:0, paddingLeft:18 }}>
                {doc.data.whatTheyWant.map((w,i) => <li key={i} style={{ fontSize:13, color:'var(--hp-text)', marginBottom:4, lineHeight:1.5 }}>{w}</li>)}
              </ul>
            </div>
          )}
          {visible.map(f => (
            <FindingCard key={f.id} finding={f} docTitle={doc.data?.docTitle || doc.filename} onUpdateFinding={updated => onUpdateFinding(doc.id, updated)}/>
          ))}
          {doc.data?.notRelevant?.length > 0 && (
            <details style={{ marginTop:8 }}>
              <summary style={{ fontSize:12, color:'var(--hp-dim)', cursor:'pointer', userSelect:'none' }}>
                {doc.data.notRelevant.length} items not relevant to Hollypaul
              </summary>
              <ul style={{ margin:'8px 0 0 16px', padding:0 }}>
                {doc.data.notRelevant.map((item,i) => <li key={i} style={{ fontSize:12, color:'var(--hp-dim)', marginBottom:4, lineHeight:1.5 }}>{item}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ── Upload Zone ───────────────────────────────────────────────────────────────

function UploadZone({ onUpload, loading }) {
  const ref = useRef();

  async function handle(files) {
    for (const file of files) {
      if (!file.name.endsWith('.pdf')) { alert('PDF files only'); continue; }
      onUpload(file);
    }
  }

  return (
    <label style={{ display:'block', cursor:'pointer' }}>
      <input ref={ref} type="file" accept=".pdf" multiple style={{ display:'none' }} onChange={e=>handle(Array.from(e.target.files||[]))}/>
      <div
        onDrop={e=>{ e.preventDefault(); handle(Array.from(e.dataTransfer.files||[])); }}
        onDragOver={e=>e.preventDefault()}
        style={{
          border:'2px dashed var(--hp-border)', borderRadius:12, padding:'32px 24px',
          textAlign:'center', transition:'border-color 0.2s',
          background: loading ? 'rgba(201,168,76,0.05)' : 'var(--hp-bg)',
        }}>
        {loading ? (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12, color:'var(--hp-dim)' }}>
            <div style={{ width:32, height:32, border:'3px solid var(--hp-border)', borderTopColor:'var(--hp-gold)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
            <div style={{ fontWeight:600 }}>Analysing with Claude…</div>
            <div style={{ fontSize:12 }}>Extracting text · Sending to Claude · This takes 20–40 seconds</div>
          </div>
        ) : (
          <>
            <Upload size={32} color="var(--hp-gold)" style={{ marginBottom:12 }}/>
            <div style={{ fontWeight:700, fontSize:15, color:'var(--hp-text)', marginBottom:6 }}>Drop AP document here or click to select</div>
            <div style={{ fontSize:13, color:'var(--hp-dim)', marginBottom:6 }}>Newsletters · Briefings · Forum summaries · Policy updates · PDF only</div>
            <div style={{ fontSize:11, color:'var(--hp-dim)', background:'var(--hp-surface)', padding:'4px 10px', borderRadius:6, display:'inline-block' }}>
              Estimated cost per document: <strong style={{ color:'var(--hp-text)' }}>~A$0.05–$0.10</strong>
            </div>
          </>
        )}
      </div>
    </label>
  );
}

// ── Summary Dashboard (clickable tiles) ──────────────────────────────────────

// claude-sonnet-4-5 pricing (USD, as of mid-2025)
const PRICE_INPUT_PER_M  = 3.00;   // $ per 1M input tokens
const PRICE_OUTPUT_PER_M = 15.00;  // $ per 1M output tokens
const USD_TO_AUD = 1.55;

function calcCost(docs) {
  const totalInput  = docs.reduce((s,d) => s + (d.usage?.inputTokens  || 0), 0);
  const totalOutput = docs.reduce((s,d) => s + (d.usage?.outputTokens || 0), 0);
  const usd = (totalInput * PRICE_INPUT_PER_M + totalOutput * PRICE_OUTPUT_PER_M) / 1_000_000;
  return { usd, aud: usd * USD_TO_AUD, totalInput, totalOutput };
}

function SummaryDashboard({ docs, activeFilter, onFilter }) {
  const allFindings = docs.flatMap(d => (d.data?.findings||[]).map(f=>({...f,_docTitle:d.data?.docTitle||d.filename})));
  const openFindings = allFindings.filter(f => (f._caseStatus||'open') !== 'resolved');
  const counts = { critical:0, high:0, medium:0, low:0 };
  openFindings.forEach(f=>{ if(counts[f.priority]!==undefined) counts[f.priority]++; });
  const resolved = allFindings.filter(f => (f._caseStatus||'open') === 'resolved').length;
  const cost = calcCost(docs);
  if (allFindings.length === 0) return null;

  const tiles = [
    { id:'all',      label:'Total Documents',  value:docs.length,         color:'var(--hp-gold)', icon:<FileText size={16}/> },
    { id:'critical', label:'Critical Actions', value:counts.critical,     color:'#ef4444',        icon:<AlertCircle size={16}/> },
    { id:'high',     label:'High Priority',    value:counts.high,         color:'#f59e0b',        icon:<AlertTriangle size={16}/> },
    { id:'open',     label:'Open Items',       value:openFindings.length, color:'#3b82f6',        icon:<Zap size={16}/> },
    { id:'resolved', label:'Resolved',         value:resolved,            color:'#22c55e',        icon:<CheckCircle size={16}/> },
  ];

  return (
    <>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(130px, 100%),1fr))', gap:10, marginBottom:cost.totalInput>0?10:24 }}>
      {tiles.map(({id,label,value,color,icon})=>{
        const isActive = activeFilter === id;
        return (
          <div key={id} onClick={()=>onFilter(isActive ? null : id)}
            style={{ background:'var(--hp-surface)', border:`2px solid ${isActive?color:'var(--hp-border)'}`, borderTop:`3px solid ${color}`, borderRadius:10, padding:'14px 16px', cursor:'pointer', transition:'all 0.15s', boxShadow: isActive?`0 0 0 2px ${color}30`:'' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontSize:11, color:isActive?color:'var(--hp-dim)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</div>
              <span style={{ color }}>{icon}</span>
            </div>
            <div style={{ fontSize:24, fontWeight:900, color, fontVariantNumeric:'tabular-nums' }}>{value}</div>
            {isActive && <div style={{ fontSize:10, color, fontWeight:700, marginTop:4 }}>▲ Filtering — click to clear</div>}
          </div>
        );
      })}
    </div>
    {cost.totalInput > 0 && (
      <div style={{ background:'var(--hp-surface)', border:'1px solid var(--hp-border)', borderRadius:10, padding:'10px 16px', marginBottom:16, display:'flex', flexWrap:'wrap', gap:16, alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:10, color:'var(--hp-dim)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>Total API Spend</div>
            <div style={{ fontSize:17, fontWeight:800, color:'#22c55e' }}>A${cost.aud.toFixed(4)} <span style={{ fontSize:11, color:'var(--hp-dim)', fontWeight:400 }}>(US${cost.usd.toFixed(4)})</span></div>
          </div>
          <div style={{ fontSize:12, color:'var(--hp-dim)' }}>{cost.totalInput.toLocaleString()} tokens in · {cost.totalOutput.toLocaleString()} out</div>
          <div style={{ fontSize:12, color:'var(--hp-dim)' }}>~A${docs.length>0?(cost.aud/docs.length).toFixed(4):'0.0000'} per doc avg</div>
        </div>
        <div style={{ fontSize:10, color:'var(--hp-dim)' }}>claude-sonnet-4-5 · US$3/M in · US$15/M out</div>
      </div>
    )}
    </>
  );
}

// ── Paste-in zone (emails / text that aren't PDFs) ───────────────────────────

function PasteZone({ onAnalyse, loading }) {
  const [show, setShow]   = useState(false);
  const [title, setTitle] = useState('');
  const [text, setText]   = useState('');

  async function go() {
    if (text.trim().length < 30) { alert('Please paste the email or document text first.'); return; }
    const ok = await onAnalyse(text, title);
    if (ok) { setText(''); setTitle(''); setShow(false); }
  }

  if (!show) {
    return (
      <div style={{ textAlign:'center', marginTop:10 }}>
        <button onClick={()=>setShow(true)} style={{ background:'none', border:'none', color:'var(--hp-dim)', fontSize:13, cursor:'pointer', textDecoration:'underline', fontFamily:'var(--font-body)' }}>
          Or paste an email / text instead of a PDF
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop:12, border:'1px solid var(--hp-border)', borderRadius:12, padding:16, background:'var(--hp-bg)' }}>
      <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Title (optional, e.g. AP email re fuel levy)"
        style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid var(--hp-border)', background:'var(--hp-surface)', color:'var(--hp-text)', fontSize:13, fontFamily:'var(--font-body)', boxSizing:'border-box', marginBottom:8 }}/>
      <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Paste the email or document text here…"
        style={{ width:'100%', minHeight:160, padding:'10px 12px', borderRadius:8, border:'1px solid var(--hp-border)', background:'var(--hp-surface)', color:'var(--hp-text)', fontSize:13, lineHeight:1.6, fontFamily:'var(--font-body)', resize:'vertical', boxSizing:'border-box', marginBottom:10 }}/>
      <div style={{ display:'flex', gap:8 }}>
        <button onClick={go} disabled={loading} style={{ padding:'9px 18px', borderRadius:8, background:'var(--hp-gold)', border:'none', color:'#000', fontWeight:700, fontSize:13, cursor:loading?'default':'pointer', fontFamily:'var(--font-body)', opacity:loading?0.6:1 }}>
          {loading ? 'Analysing…' : 'Analyse text'}
        </button>
        <button onClick={()=>setShow(false)} style={{ padding:'9px 16px', borderRadius:8, background:'none', border:'1px solid var(--hp-border)', color:'var(--hp-dim)', fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'var(--font-body)' }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Themes (what AP keeps raising across documents) ──────────────────────────

const THEME_STOPWORDS = new Set(['the','and','for','with','from','this','that','update','updates','new','rate','rates','will','your','our','are','have','has','requirement','requirements','change','changes','review','must','also','into','they','their','been','which','when','what','where','about','under','over','more','than','then','them','these','those','all','any','may','per','via','due','date','document']);

function computeThemes(docs) {
  const byCategory = {};
  const termDocs = {};
  docs.forEach(d => (d.data?.findings || []).forEach(f => {
    if (f.category) byCategory[f.category] = (byCategory[f.category] || 0) + 1;
    const words = ((f.title || '') + ' ' + (f.relevance || '')).toLowerCase().match(/[a-z]{4,}/g) || [];
    new Set(words).forEach(w => { if (!THEME_STOPWORDS.has(w)) { (termDocs[w] = termDocs[w] || new Set()).add(d.id); } });
  }));
  const categories = Object.entries(byCategory).sort((a,b)=>b[1]-a[1]);
  const topTerms = Object.entries(termDocs)
    .map(([term,set])=>({ term, docs:set.size }))
    .filter(x=>x.docs>=2)
    .sort((a,b)=>b.docs-a.docs)
    .slice(0,12);
  return { categories, topTerms };
}

function ThemesPanel({ docs, onPick }) {
  const [open, setOpen] = useState(false);
  const { categories, topTerms } = computeThemes(docs);
  if (categories.length === 0) return null;
  const chipBtn = { cursor:'pointer', fontFamily:'var(--font-body)' };

  return (
    <div style={{ background:'var(--hp-surface)', border:'1px solid var(--hp-border)', borderRadius:10, marginBottom:16 }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ padding:'10px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, fontWeight:700, color:'var(--hp-text)' }}>
          <TrendingUp size={15} color="var(--hp-gold)"/> Themes — what AP keeps raising
        </div>
        {open ? <ChevronUp size={16} color="var(--hp-dim)"/> : <ChevronDown size={16} color="var(--hp-dim)"/>}
      </div>
      {open && (
        <div style={{ padding:'0 16px 16px', borderTop:'1px solid var(--hp-border)', paddingTop:12, display:'grid', gap:14 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>By Category</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {categories.map(([cat,n])=>(
                <button key={cat} onClick={()=>onPick({ kind:'category', value:cat })} title={`Show ${n} ${cat} finding${n!==1?'s':''}`}
                  style={{ ...chipBtn, display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:99, background:'var(--hp-bg)', border:'1px solid var(--hp-border)', fontSize:12, color:'var(--hp-text)', fontWeight:600 }}>
                  {CATEGORY_ICONS[cat] || <Info size={13}/>} {cat} <strong style={{ color:'var(--hp-gold)' }}>{n}</strong>
                </button>
              ))}
            </div>
          </div>
          {topTerms.length > 0 && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Recurring across documents</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {topTerms.map(({term,docs:n})=>(
                  <button key={term} onClick={()=>onPick({ kind:'term', value:term })} title={`Show findings mentioning ${term}`}
                    style={{ ...chipBtn, padding:'3px 10px', borderRadius:99, background:'rgba(201,168,76,0.1)', border:'1px solid rgba(201,168,76,0.3)', fontSize:12, color:'var(--hp-text)', fontWeight:600 }}>
                    {term} <strong style={{ color:'var(--hp-gold)' }}>×{n}</strong>
                  </button>
                ))}
              </div>
              <div style={{ fontSize:11, color:'var(--hp-dim)', marginTop:8 }}>Terms appearing in {topTerms.length>0?'2+':'multiple'} documents — the more documents, the clearer the pattern.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function APIntelligencePage() {
  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [fetching, setFetching]   = useState(true);
  const [error, setError]         = useState('');
  const [globalFilter, setGlobalFilter] = useState(null); // null | 'critical' | 'high' | 'open' | 'resolved'
  const [view, setView] = useState('docs'); // 'docs' | 'actions'
  const [themeFilter, setThemeFilter] = useState(null); // null | { kind:'category'|'term', value }

  // Clicking a theme chip jumps to a filtered Action List
  function handlePickTheme(filter) {
    setThemeFilter(filter);
    setView('actions');
  }

  // Load from server on mount
  useEffect(() => {
    fetchDocs().then(d => { setDocs(d); setFetching(false); });
  }, []);

  // Extract text from PDF using pdfjs-dist v6
  async function extractPdfText(file) {
    try {
      const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
      GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      let text = '';
      for (let i = 1; i <= Math.min(pdf.numPages, 25); i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        text += `\n--- Page ${i} ---\n` + content.items.map(item => item.str).join(' ');
      }
      return text.slice(0, 15000); // cap at 15K chars — enough for any AP doc
    } catch (err) {
      console.warn('PDF text extraction failed, using binary:', err.message);
      return null;
    }
  }

  async function handleUpload(file) {
    // Duplicate guard — same filename already analysed?
    const dup = docs.find(d => d.filename === file.name);
    if (dup && !window.confirm(`"${file.name}" was already analysed as "${dup.data?.docTitle || file.name}". Analyse again anyway? This creates a second copy and costs another API call.`)) {
      return;
    }

    setLoading(true); setError('');
    try {
      // Read the raw PDF once — used for Drive storage
      const b64 = await new Promise((res,rej)=>{
        const r = new FileReader();
        r.onload = e => res(e.target.result.split(',')[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });

      // Extract text and send to Claude (shared helper)
      const textContent = await extractPdfText(file);
      const { analysisResult, usage } = await analyseText(
        (textContent && textContent.length > 100) ? textContent : '',
        file.name
      );

      const newId = Date.now().toString();

      // Best-effort: store the original PDF in Drive so it can be re-opened later.
      // Never blocks the analysis — a Drive/size failure just means no "View PDF" link.
      let pdfUrl = '', pdfFileId = '';
      try {
        if (file.size <= 4.5 * 1024 * 1024) {
          const fr = await fetch(STORE_URL, { method:'POST', headers:{'Content-Type':'application/json'},
            body: JSON.stringify({ action:'saveFile', id:newId, filename:file.name, pdfBase64:b64, contentType:'application/pdf' }) });
          const fj = await fr.json();
          if (fj.ok) { pdfUrl = fj.viewUrl || ''; pdfFileId = fj.fileId || ''; }
        }
      } catch { /* keep the analysis even if Drive storage fails */ }

      const newDoc = {
        id: newId,
        filename: file.name,
        uploadedAt: new Date().toISOString(),
        data: analysisResult,
        pdfUrl, pdfFileId,
        usage: usageToCost(usage),
      };
      await serverSave(newDoc);
      setDocs(prev => [newDoc, ...prev]);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePasteText(text, title) {
    setLoading(true); setError('');
    try {
      const filename = (title && title.trim()) ? title.trim() : 'Pasted text';
      const { analysisResult, usage } = await analyseText(text, filename);
      const newDoc = {
        id: Date.now().toString(),
        filename,
        uploadedAt: new Date().toISOString(),
        data: analysisResult,
        pdfUrl: '', pdfFileId: '',
        usage: usageToCost(usage),
      };
      await serverSave(newDoc);
      setDocs(prev => [newDoc, ...prev]);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function handleAttachPdf(docId, file) {
    // Upload the original PDF and patch the existing record — no re-analysis,
    // so all findings, notes, tasks and case statuses are preserved.
    const b64 = await new Promise((res,rej)=>{
      const r = new FileReader();
      r.onload = e => res(e.target.result.split(',')[1]);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const fr = await fetch(STORE_URL, { method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ action:'saveFile', id:docId, filename:file.name, pdfBase64:b64, contentType:'application/pdf' }) });
    const fj = await fr.json();
    if (!fj.ok || !fj.viewUrl) throw new Error('Could not store PDF');
    const updatedDocs = docs.map(d => d.id === docId ? { ...d, pdfUrl:fj.viewUrl, pdfFileId:fj.fileId } : d);
    setDocs(updatedDocs);
    const updatedDoc = updatedDocs.find(d => d.id === docId);
    if (updatedDoc) await serverSave(updatedDoc);
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this document and its findings?')) return;
    await serverDelete(id);
    setDocs(prev => prev.filter(d=>d.id!==id));
  }

  async function handleUpdateFinding(docId, updatedFinding) {
    // Save full finding object (position, tasks, caseStatus, actioned)
    const updatedDocs = docs.map(d => {
      if (d.id !== docId) return d;
      return { ...d, data: { ...d.data, findings: d.data.findings.map(f => f.id === updatedFinding.id ? updatedFinding : f) }};
    });
    setDocs(updatedDocs);
    // Persist full doc to server
    const updatedDoc = updatedDocs.find(d => d.id === docId);
    if (updatedDoc) await serverSave(updatedDoc);
  }

  // Map filter ids to finding predicates
  function filterMatches(f) {
    if (!globalFilter || globalFilter === 'all') return true;
    if (globalFilter === 'open') return (f._caseStatus || 'open') !== 'resolved';
    if (globalFilter === 'resolved') return (f._caseStatus || 'open') === 'resolved';
    return f.priority === globalFilter;
  }

  const filteredDocs = docs.map(d => ({
    ...d,
    data: d.data ? { ...d.data, findings: (d.data.findings||[]).filter(f => filterMatches(f) && themeMatches(f)) } : d.data,
  })).filter(d => !(globalFilter || themeFilter) || !d.data || d.data.findings.length > 0);

  // Theme chip filter (category or keyword) layered on top of the tile filter
  function themeMatches(f) {
    if (!themeFilter) return true;
    if (themeFilter.kind === 'category') return f.category === themeFilter.value;
    const hay = ((f.title||'') + ' ' + (f.relevance||'')).toLowerCase();
    return hay.includes(themeFilter.value.toLowerCase());
  }

  // Flat, cross-document action list — every finding, sorted by urgency
  const deadlineTime = f => { const t = f.deadline ? Date.parse(f.deadline) : NaN; return isNaN(t) ? Infinity : t; };
  const allFindings = docs
    .flatMap(d => (d.data?.findings || []).map(f => ({ f, docId:d.id, docTitle:d.data?.docTitle || d.filename })))
    .filter(x => filterMatches(x.f) && themeMatches(x.f))
    .sort((a,b) => {
      const ar = (a.f._caseStatus||'open')==='resolved' ? 1 : 0;
      const br = (b.f._caseStatus||'open')==='resolved' ? 1 : 0;
      if (ar !== br) return ar - br;                                              // resolved last
      const ap = PRIORITY_CONFIG[a.f.priority]?.order ?? 9;
      const bp = PRIORITY_CONFIG[b.f.priority]?.order ?? 9;
      if (ap !== bp) return ap - bp;                                             // critical → low
      return deadlineTime(a.f) - deadlineTime(b.f);                             // soonest deadline first
    });

  return (
    <div style={{ padding:'24px', maxWidth:1000, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ margin:0, fontWeight:900, fontSize:22, color:'var(--hp-text)' }}>AP Intelligence</h1>
        <div style={{ color:'var(--hp-dim)', fontSize:13, marginTop:4 }}>
          Upload Australia Post newsletters, briefings and forum documents — Claude extracts what matters to Hollypaul
        </div>
      </div>

      <SummaryDashboard docs={docs} activeFilter={globalFilter} onFilter={setGlobalFilter}/>

      <ThemesPanel docs={docs} onPick={handlePickTheme}/>

      {globalFilter && (
        <div style={{ marginBottom:16, padding:'8px 14px', background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.25)', borderRadius:8, fontSize:13, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ color:'#3b82f6', fontWeight:600 }}>Filtering: {globalFilter} items only</span>
          <button onClick={()=>setGlobalFilter(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#3b82f6', fontWeight:700, fontSize:12, fontFamily:'var(--font-body)' }}>✕ Clear filter</button>
        </div>
      )}

      <UploadZone onUpload={handleUpload} loading={loading}/>
      <PasteZone onAnalyse={handlePasteText} loading={loading}/>

      {error && (
        <div style={{ marginTop:16, padding:'12px 16px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8, color:'#ef4444', fontSize:13 }}>
          {error}
        </div>
      )}

      {themeFilter && (
        <div style={{ marginTop:16, padding:'8px 14px', background:'rgba(201,168,76,0.1)', border:'1px solid rgba(201,168,76,0.3)', borderRadius:8, fontSize:13, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ color:'var(--hp-gold)', fontWeight:600 }}>
            Theme: {themeFilter.kind === 'category' ? `${themeFilter.value} findings` : `mentioning “${themeFilter.value}”`} — {allFindings.length} match{allFindings.length!==1?'es':''}
          </span>
          <button onClick={()=>setThemeFilter(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--hp-gold)', fontWeight:700, fontSize:12, fontFamily:'var(--font-body)' }}>✕ Clear theme</button>
        </div>
      )}

      {docs.length > 0 && (
        <div style={{ marginTop:24 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap', marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:14, color:'var(--hp-dim)', textTransform:'uppercase', letterSpacing:'0.07em' }}>
              {view === 'docs'
                ? `${filteredDocs.length} Document${filteredDocs.length!==1?'s':''} ${globalFilter?`· ${globalFilter} filter active`:'Analysed'}`
                : `${allFindings.length} Action${allFindings.length!==1?'s':''} ${globalFilter?`· ${globalFilter} filter active`:'· sorted by urgency'}`}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {[{id:'docs',label:'By Document'},{id:'actions',label:'Action List'}].map(v=>(
                <button key={v.id} onClick={()=>setView(v.id)}
                  style={{ padding:'5px 14px', borderRadius:8, border:`2px solid ${view===v.id?'var(--hp-gold)':'var(--hp-border)'}`, background:view===v.id?'rgba(201,168,76,0.1)':'var(--hp-bg)', color:view===v.id?'var(--hp-gold)':'var(--hp-dim)', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'var(--font-body)' }}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {view === 'docs'
            ? filteredDocs.map(d => <DocCard key={d.id} doc={d} onDelete={handleDelete} onUpdateFinding={handleUpdateFinding} onAttachPdf={handleAttachPdf} globalFilter={globalFilter}/>)
            : (allFindings.length > 0
                ? allFindings.map(({f,docId,docTitle}) => <FindingCard key={docId+'_'+f.id} finding={f} docTitle={docTitle} onUpdateFinding={updated => handleUpdateFinding(docId, updated)}/>)
                : <div style={{ textAlign:'center', padding:'24px 0', color:'var(--hp-dim)', fontSize:13 }}>No actions match the current filter.</div>)}
        </div>
      )}

      {fetching && (
        <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)' }}>
          <div style={{ width:28, height:28, border:'3px solid var(--hp-border)', borderTopColor:'var(--hp-gold)', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
          <div style={{ fontSize:13 }}>Loading saved documents…</div>
        </div>
      )}
      {docs.length === 0 && !loading && !fetching && (
        <div style={{ textAlign:'center', padding:'40px 0', color:'var(--hp-dim)' }}>
          <FileText size={40} style={{ opacity:0.3, marginBottom:12 }}/>
          <div style={{ fontWeight:600 }}>No documents yet</div>
          <div style={{ fontSize:13, marginTop:4 }}>Upload your first AP document above</div>
        </div>
      )}
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}
